/* Native dialog enhancement of the same details menu; never locks document scrolling. */
(() => {
  if (customElements.get('fahrkontur-navigation')) return;

  class FahrkonturNavigation extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-menu-details]');
      this.trigger = this.querySelector('[data-menu-trigger]');
      this.panel = this.querySelector('[data-menu-panel]');
      if (!this.details || !this.trigger || !this.panel) return;

      this.abort = new AbortController();
      const signal = this.abort.signal;
      const listen = (element, name, callback, options = {}) =>
        element.addEventListener(name, callback, { ...options, signal });
      // Keep keyboard focus visible, including after native dialog dismissal.
      // Touch/click focus restoration must not leave a rectangular header tile.
      listen(document, 'pointerdown', () => { this.dataset.input = 'pointer'; }, { capture: true });
      listen(document, 'keydown', (event) => {
        if (event.key === 'Tab' || event.key.startsWith('Arrow')) this.dataset.input = 'keyboard';
      }, { capture: true });
      const nativeDialog = typeof this.panel.showModal === 'function';

      listen(this.details, 'toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.details.open));
        this.trigger.setAttribute('aria-label', this.details.open ? 'Menü schließen' : 'Menü öffnen');
      });

      if (nativeDialog) {
        this.dataset.ready = 'true';
        listen(this.trigger, 'click', (event) => {
          event.preventDefault();
          if (this.panel.open) this.closeMenu();
          else this.openMenu();
        });
        listen(this.panel, 'close', () => {
          if (this.panel.open) return;
          clearTimeout(this.closeTimer);
          delete this.panel.dataset.closing;
          this.details.open = false;
          this.trigger.setAttribute('aria-expanded', 'false');
        });
        listen(this.panel, 'cancel', (event) => {
          event.preventDefault();
          this.closeMenu();
        });
        listen(this.panel, 'keydown', (event) => {
          if (event.key !== 'Tab') return;
          const controls = [...this.panel.querySelectorAll('a[href],button:not([disabled]),summary,input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
            .filter((control) => control.tabIndex >= 0 && control.getClientRects().length && getComputedStyle(control).visibility !== 'hidden');
          const first = controls[0], last = controls[controls.length - 1];
          if (!first) return;
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });
        listen(this.panel, 'click', (event) => {
          if (event.target.closest('[data-menu-close]')) this.closeMenu();
          const link = event.target.closest('a[href]');
          // Native localization forms need their selection links until submission.
          // Release the native dialog before a chapter link changes the journey.
          if (link && !link.closest('localization-form')) this.closeMenu(true);
          if (event.target === this.panel) {
            const bounds = this.panel.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right ||
                event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeMenu();
          }
        });
        listen(window, 'pagehide', () => this.closeMenu(true));
        listen(document, 'shopify:section:unload', (event) => {
          if (event.target.contains(this)) this.closeMenu(true);
        });
        if (this.details.open && !this.panel.open) this.openMenu();
      }

      // Theme-editor header reloads replace only this link, not the existing cart drawer.
      const cartLink = this.querySelector('#cart-icon-bubble');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartLink?.getAttribute('aria-haspopup') !== 'dialog' && cartDrawer?.setHeaderCartIconAccessibility)
        cartDrawer.setHeaderCartIconAccessibility();
    }

    openMenu() {
      clearTimeout(this.closeTimer);
      delete this.panel.dataset.closing;
      this.details.open = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.trigger.setAttribute('aria-label', 'Menü schließen');
      const journey = document.querySelector('fahrkontur-v7[data-ready]');
      let currentHash = location.hash || '#FAHRKONTUR';
      if (journey) {
        const atConfiguration = journey.dataset.guideScroll === 'config' ||
          (journey.dataset.guided !== 'true' && journey.dataset.chapter === '4');
        currentHash = atConfiguration ? '#Konfigurator' : '#FAHRKONTUR';
      }
      this.panel.querySelectorAll('.fk-nav-primary a').forEach((link) => {
        const target = new URL(link.href, location.href);
        const current = target.pathname === location.pathname && target.hash === currentHash;
        if (current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (!this.panel.open) this.panel.showModal();
      this.panel.scrollTop = 0;
      this.panel.querySelector('[data-menu-close]')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('fahrkontur:navigation-open'));
    }

    closeMenu(immediate = false) {
      const finish = () => {
        clearTimeout(this.closeTimer);
        if (this.panel?.open) this.panel.close();
        if (this.panel) delete this.panel.dataset.closing;
        if (this.details) this.details.open = false;
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.trigger?.setAttribute('aria-label', 'Menü öffnen');
      };
      if (immediate || !this.panel?.open || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      if (this.panel.hasAttribute('data-closing')) return;
      this.panel.dataset.closing = 'true';
      this.closeTimer = setTimeout(finish, 160);
    }

    disconnectedCallback() {
      this.closeMenu(true);
      this.abort?.abort();
      delete this.dataset.ready;
    }
  }

  customElements.define('fahrkontur-navigation', FahrkonturNavigation);
})();
